import type { tFunnelInfo, tLeadUsers } from '../../components/Chat/Chat.types';
import { eUserRole } from '../UserProvider/User.enums';
import type { tUserRoleSettlement } from '../UserProvider/User.types';
import type { tWorkspaceUserUpdate } from './Workspace.types';
import type {
  tCurrentWorkspaceInfo,
  tCurrentWorkspaceInfoResponse,
  tWorkspaceUserInfo,
} from './Workspace.types';

/**
 * Pure construction/normalization helpers for `tCurrentWorkspaceInfo`.
 *
 * This module intentionally has zero React / ahooks / routing imports so it
 * can be imported by unit tests without dragging in the full `Workspace.hook`
 * dependency graph. That isolation is what makes AC9 from PR #2148 directly
 * unit-testable (previous attempts via `renderHook(useWorkspace)` OOMed the
 * vitest pool because of cascading effects from `useLocalStorageState`,
 * `useSearchParams`, and multi-effect chains).
 *
 * See `docs/business-rules/onboarding-re-entry-rules.md` for the canonical
 * rule that `workspaceInfo.onboardingCompleted` is the frontend source of
 * truth for onboarding completion and must always be a concrete boolean
 * in-context (never `undefined`).
 */

/**
 * Build the canonical `tCurrentWorkspaceInfo` from a BFF response.
 *
 * Use at every construction site where a `tCurrentWorkspaceInfoResponse`
 * arrives from the backend — the primary mount-time fetch, every secondary
 * refetch triggered by a user-management action, and the post-payment
 * activation flow write.
 *
 * @param response - The BFF response from `GET /workspace/:id`.
 * @param users - The deduplicated users map already built by the caller.
 *   The helper does not deduplicate so caller-specific logic (invitation
 *   state overrides, recent-manual-update protection) stays where it belongs.
 * @returns A `tCurrentWorkspaceInfo` with `onboardingCompleted` guaranteed
 *   to be a concrete boolean. Legacy workspaces whose response omits the
 *   field get `false` via nullish coalescing.
 */
export function buildWorkspaceInfo(
  response: tCurrentWorkspaceInfoResponse,
  users: { [userAuthId: string]: tWorkspaceUserInfo },
): tCurrentWorkspaceInfo {
  return {
    name: response.name,
    phoneNumber: response.phoneNumber,
    isActive: response.isActive,
    users,
    settingUp: response.settingUp,
    createdAt: response.createdAt,
    industryType: response.industryType,
    userManagementV2: response.userManagementV2,
    onboardingCompleted: response.onboardingCompleted ?? false,
  };
}

/**
 * Coerce a `tCurrentWorkspaceInfo` parsed from `localStorage['workspaceInfo']`
 * into the canonical shape.
 *
 * Protects against cached blobs written before `onboardingCompleted` was
 * added (pre-RET-796 sessions) or by older code paths that skipped the
 * normalization in `buildWorkspaceInfo`. Any such blob would land in context
 * with `onboardingCompleted: undefined`, which makes the auto-setup guard's
 * `!workspaceInfo?.onboardingCompleted` check truthy and can bounce a
 * completed user into `/onboarding` during the cache-read → fetch-resolve
 * window.
 *
 * @param parsed - The parsed `tCurrentWorkspaceInfo` from localStorage.
 * @returns A shape-normalized `tCurrentWorkspaceInfo` with all other fields
 *   preserved verbatim.
 */
export function normalizeWorkspaceInfo(
  parsed: tCurrentWorkspaceInfo,
): tCurrentWorkspaceInfo {
  return {
    ...parsed,
    onboardingCompleted: parsed.onboardingCompleted ?? false,
  };
}

/**
 * BUG-534 U4: pure functional setter for `funnelData` updates triggered by a
 * `workspace.users.updated` socket event. Replaces the previous in-place
 * mutation under a raw `localStorage.setItem` (which bypassed ahooks'
 * SYNC_STORAGE_EVENT_NAME and starved live consumers like `Chat.hook` and
 * `ChatInfoAttributes`).
 *
 * Behavior:
 *   - Narrows each updated user to the `tLeadUsers` shape (`{auth_id, name}`).
 *     Round-2 A4 verified no production consumer reads role/uuid/autoAssignable
 *     off `funnelData[].users[]`; the rich shape the old code wrote was dead
 *     data.
 *   - Preserves every reference unchanged when nothing matched, so React's
 *     reconciliation can short-circuit downstream re-renders.
 *   - On matched funnels, bumps `cache_version` and clears `users_stale`
 *     (this write is authoritative for the affected funnel's users[], so the
 *     lazy refetch in `Chat.hook.resetFunnelInfo` would be redundant).
 *
 * Pure: no I/O, never throws, idempotent under the same event applied twice.
 *
 * @param prev - The current `funnelData` cache (from the ahooks setter).
 * @param eventUsers - The `users[]` array from the
 *   `tWorkspaceUsersUpdatedEvent` payload.
 * @returns Either `prev` unchanged (if no funnel had a matching user) or a
 *   new array with the affected funnels updated.
 */
export function applyWorkspaceUsersUpdateToFunnelData(
  prev: tFunnelInfo[] | undefined,
  eventUsers: tWorkspaceUserUpdate[],
): tFunnelInfo[] {
  if (!Array.isArray(prev) || prev.length === 0) return prev ?? [];
  const updatedUsers: tLeadUsers[] = eventUsers.map((user) => ({
    auth_id: user.id,
    name: user.name,
  }));
  let changed = false;
  const next = prev.map((funnel) => {
    const currentUsers = funnel.users ?? [];
    if (currentUsers.length === 0) return funnel;
    let funnelChanged = false;
    const nextUsers = currentUsers.map((u) => {
      const match = updatedUsers.find((eu) => eu.auth_id === u.auth_id);
      // Preserve reference (and any legacy-wider shape that may still be in
      // localStorage from pre-U4 writes) when the user's name didn't change.
      // Narrowing only fires on real updates per the A4 dead-data analysis.
      if (!match || match.name === u.name) return u;
      funnelChanged = true;
      return match;
    });
    if (!funnelChanged) return funnel;
    changed = true;
    return {
      ...funnel,
      users: nextUsers,
      users_stale: false,
      cache_version: (funnel.cache_version ?? 0) + 1,
    };
  });
  return changed ? next : prev;
}

/**
 * AGI-320: the caller's role as a workspace's own user list states it, or
 * `undefined` when the list does not name the caller with a role this app
 * knows.
 *
 * `undefined` is the honest answer for a caller the list leaves out: SELLER is
 * the default, not their role. Callers then leave the role unresolved (see
 * `isRoleResolved`) instead of resolving SELLER. Read the caller only from a
 * list the lookup returned (fresh or cached), never from `workspaceInfo.users`
 * later: socket events fill a missing role with SELLER.
 */
export function findListedRole(
  users: readonly Pick<tWorkspaceUserInfo, 'authId' | 'role'>[],
  userId: string | undefined,
): eUserRole | undefined {
  if (!userId) return undefined;
  const role: unknown = users.find((user) => user.authId === userId)?.role;
  return Object.values(eUserRole).find((known) => known === role);
}

/**
 * Whether `currentUserRole` is the caller's role in `workspaceId`, i.e. it was
 * read from that workspace's own user list (fresh, or the cached copy of it).
 *
 * The role alone CANNOT tell you (AGI-316 review finding): UserProvider
 * initializes `currentUserRole` to SELLER, so there is no empty state to
 * detect and a guard reading the role never guards. `workspaceInfo` cannot tell
 * you either (AGI-320): it arrives even when the list leaves the caller out, and
 * then the role is still the default.
 *
 * The positive signal is the settlement WorkspaceProvider writes when the
 * lookup ends: resolved only when the list named the caller, and only for the
 * workspace it describes, since it outlives a workspace change until the
 * provider's effect clears it. Exposed as `isRoleResolved` on the workspace and
 * ACL contexts. Consumers: role-gated menus, the Optimization Center's decide
 * buttons (AGI-316), the WhatsApp API credits gate (RET-2027) and analytics
 * events read by role (`front.analytics.general.viewed`, RET-1985).
 */
export function isRoleResolved(
  settlement: tUserRoleSettlement | undefined,
  workspaceId: string | undefined,
): boolean {
  if (!workspaceId || !settlement) return false;
  return settlement.workspaceId === workspaceId && settlement.isRoleResolved;
}
